import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  AccountBalance,
  Person,
  Close,
  Send,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { getApiUrl } from '../config';
import TransferDialog from './TransferDialog';
import TransactionLedger from './TransactionLedger';
import {
  GlassCard,
  GlassButton,
  GlassDialog,
  GlassTextField,
  GlassInfoCard,
  GlassChip,
  GlassIconButton,
} from './ui/GlassComponents';

const PlayerBankAccounts = ({ sessionData }) => {
  const [accounts, setAccounts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankOfferings, setBankOfferings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Transfer dialog state
  const [transferDialog, setTransferDialog] = useState(false);
  const [transferSource, setTransferSource] = useState(null);
  
  // Transaction ledger state
  const [transactionLedgerDialog, setTransactionLedgerDialog] = useState(false);
  const [ledgerAccount, setLedgerAccount] = useState(null);
  
  // Dialog states
  const [openAccountDialog, setOpenAccountDialog] = useState(false);
  const [closeAccountDialog, setCloseAccountDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedOffering, setSelectedOffering] = useState('');
  const [closeReason, setCloseReason] = useState('');

  useEffect(() => {
    if (sessionData?.selectedDisplayName) {
      fetchPlayerAccounts();
      fetchAllBanks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData]);

  const fetchPlayerAccounts = async () => {
    if (!sessionData?.selectedDisplayName) return;
    
    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/players/${sessionData.selectedDisplayName}/accounts`), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch player accounts');
      }
      
      const data = await response.json();
      setAccounts(data);
    } catch (err) {
      setError('Failed to load accounts: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBanks = async () => {
    try {
      const response = await fetch(getApiUrl('/banks'), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch banks');
      }
      
      const data = await response.json();
      setBanks(data.banks || []);
    } catch (err) {
      setError('Failed to load banks: ' + err.message);
    }
  };

  const fetchBankOfferings = async (bankId) => {
    try {
      const response = await fetch(getApiUrl(`/banks/${bankId}/offerings?activeOnly=true`), {
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch bank offerings');
      }
      
      const data = await response.json();
      setBankOfferings(data);
    } catch (err) {
      setError('Failed to load bank offerings: ' + err.message);
    }
  };

  const handleOpenAccountDialog = async () => {
    setSelectedBank(null);
    setSelectedOffering('');
    setBankOfferings([]);
    setOpenAccountDialog(true);
  };

  const handleBankSelection = async (bankId) => {
    setSelectedBank(bankId);
    setSelectedOffering('');
    await fetchBankOfferings(bankId);
  };

  const handleOpenAccount = async () => {
    if (!selectedBank || !selectedOffering) {
      setError('Please select a bank and account offering');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/banks/${selectedBank}/accounts/open`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          offeringId: parseInt(selectedOffering),
          openedBy: sessionData.selectedDisplayName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open account');
      }

      setOpenAccountDialog(false);
      await fetchPlayerAccounts();
      setError('');
      setSuccess('Bank account opened successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess('');
      setError('Failed to open account: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAccountDialog = (account) => {
    setSelectedAccount(account);
    setCloseReason('');
    setCloseAccountDialog(true);
  };

  const handleCloseAccount = async () => {
    if (!selectedAccount) return;

    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/banks/${selectedAccount.bankId}/accounts/${selectedAccount.accountNumber}/close`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`,
        },
        body: JSON.stringify({
          closedBy: sessionData.selectedDisplayName,
          reason: closeReason || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to close account');
      }

      setCloseAccountDialog(false);
      await fetchPlayerAccounts();
      setError('');
      setSuccess('Bank account closed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess('');
      setError('Failed to close account: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat().format(amount);
  };

  const formatInterestRate = (basisPoints) => {
    return `${(basisPoints / 100).toFixed(2)}%`;
  };

  const handleTransferFromAccount = (account) => {
    setTransferSource(account);
    setTransferDialog(true);
  };

  const handleViewTransactions = (account) => {
    setLedgerAccount(account);
    setTransactionLedgerDialog(true);
  };

  const handleTransferSuccess = (result) => {
    setSuccess(`Transfer completed successfully: ${result.transfer.amount} ${result.transfer.currencySymbol}`);
    setError('');
    setTransferDialog(false);
    setTransferSource(null);
    // Refresh account data
    fetchPlayerAccounts();
  };

  const handleSetAsDefault = async (account) => {
    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/banks/${account.bankId}/accounts/${account.accountNumber}/set-default`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionData.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to set default account');
      }

      await fetchPlayerAccounts();
      setError('');
      setSuccess(`${account.accountNumber} is now your default account!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setSuccess('');
      setError('Failed to set default account: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAllCurrencies = () => {
    // Get all unique currencies from all accounts
    const currencySet = new Set();
    accounts.forEach(account => {
      account.balances.forEach(balance => {
        currencySet.add(JSON.stringify({
          symbol: balance.currencySymbol,
          name: balance.currencyName
        }));
      });
    });
    return Array.from(currencySet).map(str => JSON.parse(str));
  };

  if (!sessionData?.selectedDisplayName) {
    return (
      <GlassInfoCard severity="info">
        Please select a character to view your bank accounts.
      </GlassInfoCard>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
          My Bank Accounts
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <GlassButton
            variant="glass"
            size="small"
            onClick={fetchPlayerAccounts}
            disabled={loading}
          >
            <RefreshIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            Refresh
          </GlassButton>
          <GlassButton
            variant="glass"
            onClick={handleOpenAccountDialog}
            disabled={loading}
          >
            <Add sx={{ fontSize: '1rem', mr: 0.5 }} />
            Open Account
          </GlassButton>
        </Box>
      </Box>

      {error && (
        <GlassInfoCard severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </GlassInfoCard>
      )}

      {success && (
        <GlassInfoCard severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </GlassInfoCard>
      )}

      {loading && !accounts.length ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.8)' }} />
        </Box>
      ) : accounts.length === 0 ? (
        <GlassInfoCard severity="info">
          You don't have any bank accounts yet. Open your first account to get started!
        </GlassInfoCard>
      ) : (
        <Box>
          {/* Group accounts by bank */}
          {Object.entries(
            accounts.reduce((grouped, account) => {
              const bankKey = `${account.bankId}-${account.bankName}`;
              if (!grouped[bankKey]) {
                grouped[bankKey] = {
                  bankId: account.bankId,
                  bankName: account.bankName,
                  accounts: []
                };
              }
              grouped[bankKey].accounts.push(account);
              return grouped;
            }, {})
          ).map(([bankKey, bankGroup]) => (
            <Box key={bankKey} mb={4}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', color: 'white', fontWeight: 600, gap: 1 }}>
                <AccountBalance sx={{ color: 'rgba(102, 126, 234, 0.9)' }} />
                {bankGroup.bankName}
                <GlassChip
                  size="small"
                  variant="info"
                >
                  {`${bankGroup.accounts.length} account${bankGroup.accounts.length !== 1 ? 's' : ''}`}
                </GlassChip>
              </Typography>

              <Grid container spacing={2}>
                {bankGroup.accounts.map((account) => (
                  <Grid item xs={12} md={6} lg={4} key={account.id}>
                    <GlassCard sx={{ height: '100%' }}>
                      <Box sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="h6" component="div" sx={{ color: 'white', fontWeight: 600 }}>
                                Account {account.accountNumber.split('-')[1]}
                              </Typography>
                              {account.isDefault && (
                                <GlassChip
                                  size="small"
                                  variant="info"
                                  sx={{ height: 20, fontSize: '0.75rem' }}
                                >
                                  Default
                                </GlassChip>
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {account.accountNumber}
                            </Typography>
                          </Box>
                          <GlassIconButton
                            size="small"
                            onClick={() => handleCloseAccountDialog(account)}
                            disabled={account.balances.some(b => b.balance !== 0)}
                            sx={{
                              color: 'rgba(255, 107, 107, 0.9)',
                              border: '1px solid rgba(255, 107, 107, 0.3)',
                              '&:hover': {
                                background: 'rgba(255, 107, 107, 0.15)',
                              },
                              '&:disabled': {
                                opacity: 0.5,
                              }
                            }}
                          >
                            <Close />
                          </GlassIconButton>
                        </Box>

                        {account.offering && (
                          <Box mb={2}>
                            <Typography variant="subtitle2" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
                              {account.offering.name}
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                              <GlassChip
                                size="small"
                                variant="success"
                              >
                                {`${formatInterestRate(account.offering.interestRateBasisPoints)} Interest`}
                              </GlassChip>
                              {account.offering.overdraftAllowed && (
                                <GlassChip
                                  size="small"
                                  variant="info"
                                >
                                  Overdraft Available
                                </GlassChip>
                              )}
                              {/* Min/Max Balance Chips */}
                              {account.offering.minimumBalance > 0 && (
                                <GlassChip
                                  size="small"
                                  variant="warning"
                                >
                                  Min: {formatCurrency(account.offering.minimumBalance)}
                                </GlassChip>
                              )}
                              {account.offering.maximumBalance && (
                                <GlassChip
                                  size="small"
                                  variant="info"
                                >
                                  Max: {formatCurrency(account.offering.maximumBalance)}
                                </GlassChip>
                              )}
                              {/* Maintenance Fee Chip */}
                              {account.offering.maintenanceFeeAmount > 0 && (
                                <GlassChip
                                  size="small"
                                  variant="error"
                                >
                                  Fee: {formatCurrency(account.offering.maintenanceFeeAmount)}/week
                                </GlassChip>
                              )}
                              {/* Overdraft Details */}
                              {account.offering.overdraftAllowed && account.offering.overdraftLimit > 0 && (
                                <GlassChip
                                  size="small"
                                  variant="secondary"
                                >
                                  Overdraft: {formatCurrency(account.offering.overdraftLimit)}
                                </GlassChip>
                              )}
                              {account.offering.overdraftAllowed && account.offering.overdraftFeeAmount > 0 && (
                                <GlassChip
                                  size="small"
                                  variant="error"
                                >
                                  Overdraft Fee: {formatCurrency(account.offering.overdraftFeeAmount)}
                                </GlassChip>
                              )}
                            </Box>
                          </Box>
                        )}

                        <Typography variant="subtitle2" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
                          Balances:
                        </Typography>
                        {account.balances.length === 0 ? (
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            No balances
                          </Typography>
                        ) : (
                          account.balances.map((balance) => (
                            <Typography key={balance.currencySymbol} variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                              {formatCurrency(balance.balance)} {balance.currencySymbol}
                            </Typography>
                          ))
                        )}

                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }} display="block" mt={1}>
                          Opened: {new Date(account.createdAt * 1000).toLocaleDateString()}
                        </Typography>

                        <Box display="flex" justifyContent="flex-end" gap={1} mt={2} flexWrap="wrap">
                          {!account.isDefault && (
                            <GlassButton
                              variant="glass"
                              size="small"
                              onClick={() => handleSetAsDefault(account)}
                              disabled={loading}
                            >
                              <Person sx={{ fontSize: '1rem', mr: 0.5 }} />
                              Default
                            </GlassButton>
                          )}
                          <GlassButton
                            variant="glass"
                            size="small"
                            onClick={() => handleViewTransactions(account)}
                          >
                            <InfoIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                            History
                          </GlassButton>
                          <GlassButton
                            variant="glass"
                            size="small"
                            onClick={() => handleTransferFromAccount(account)}
                            disabled={!account.balances.some(b => b.balance > 0)}
                          >
                            <Send sx={{ fontSize: '1rem', mr: 0.5 }} />
                            Transfer
                          </GlassButton>
                        </Box>
                      </Box>
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      )}

      {/* Open Account Dialog */}
      <GlassDialog
        open={openAccountDialog}
        onClose={() => setOpenAccountDialog(false)}
        title="Open Bank Account"
        maxWidth="md"
        actions={
          <>
            <GlassButton variant="glass" onClick={() => setOpenAccountDialog(false)}>
              Cancel
            </GlassButton>
            <GlassButton variant="glass" onClick={handleOpenAccount} disabled={!selectedBank || !selectedOffering || loading}>
              {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Open Account'}
            </GlassButton>
          </>
        }
      >
        <FormControl fullWidth margin="normal"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: 'white',
              background: 'rgba(255, 255, 255, 0.08)',
              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.25)' },
              '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
              '&.Mui-focused fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
            },
            '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
            '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(255, 255, 255, 0.95)' },
            '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.6)' },
          }}
        >
          <InputLabel>Select Bank</InputLabel>
          <Select
            value={selectedBank || ''}
            onChange={(e) => handleBankSelection(e.target.value)}
            label="Select Bank"
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: 'rgba(30, 30, 40, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  '& .MuiMenuItem-root': {
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                    '&.Mui-selected': {
                      bgcolor: 'rgba(255, 255, 255, 0.15)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                    },
                  },
                },
              },
            }}
          >
            {banks.map((bank) => (
              <MenuItem key={bank.id} value={bank.id}>
                {bank.name} ({bank.jurisdictionNation})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedBank && (
          <FormControl fullWidth margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                background: 'rgba(255, 255, 255, 0.08)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.25)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.35)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(255, 255, 255, 0.95)' },
              '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.6)' },
            }}
          >
            <InputLabel>Select Account Type</InputLabel>
            <Select
              value={selectedOffering}
              onChange={(e) => setSelectedOffering(e.target.value)}
              label="Select Account Type"
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: 'rgba(30, 30, 40, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    '& .MuiMenuItem-root': {
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                      '&.Mui-selected': {
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                      },
                    },
                  },
                },
              }}
            >
              {bankOfferings.map((offering) => {
                const existingAccountsWithOffering = accounts.filter(acc =>
                  acc.bankId === selectedBank && acc.offering?.id === offering.id
                ).length;

                return (
                  <MenuItem key={offering.id} value={offering.id}>
                    <Box sx={{ width: '100%' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body1">
                          {offering.name}
                        </Typography>
                        {existingAccountsWithOffering > 0 && (
                          <GlassChip
                            size="small"
                            variant="info"
                          >
                            {`${existingAccountsWithOffering} existing`}
                          </GlassChip>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {formatInterestRate(offering.interestRateBasisPoints)} interest
                        {offering.maintenanceFeeAmount > 0 && ` • ${formatCurrency(offering.maintenanceFeeAmount)} monthly fee`}
                        {offering.minimumBalance > 0 && ` • ${formatCurrency(offering.minimumBalance)} min balance`}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}

        {selectedBank && selectedOffering && (
          <Box sx={{ mt: 2 }}>
            {/* Show existing accounts at this bank */}
            {(() => {
              const existingAtBank = accounts.filter(acc => acc.bankId === selectedBank);
              if (existingAtBank.length > 0) {
                return (
                  <GlassInfoCard severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                      <strong>You already have {existingAtBank.length} account{existingAtBank.length !== 1 ? 's' : ''} at this bank:</strong>
                    </Typography>
                    {existingAtBank.map(acc => (
                      <Typography key={acc.id} variant="caption" display="block" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        • {acc.accountNumber} ({acc.offering?.name || 'Basic Account'})
                      </Typography>
                    ))}
                  </GlassInfoCard>
                );
              }
              return null;
            })()}

            <GlassInfoCard severity="info">
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                Make sure to review the account terms before opening. You can open multiple accounts at the same bank for different purposes.
              </Typography>
            </GlassInfoCard>
          </Box>
        )}
      </GlassDialog>

      {/* Close Account Dialog */}
      <GlassDialog
        open={closeAccountDialog}
        onClose={() => setCloseAccountDialog(false)}
        title="Close Bank Account"
        maxWidth="sm"
        actions={
          <>
            <GlassButton variant="glass" onClick={() => setCloseAccountDialog(false)}>
              Cancel
            </GlassButton>
            <GlassButton
              variant="glass"
              onClick={handleCloseAccount}
              sx={{ color: 'rgba(255, 107, 107, 0.9)' }}
              disabled={loading || (selectedAccount && selectedAccount.balances.some(b => b.balance !== 0))}
            >
              {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Close Account'}
            </GlassButton>
          </>
        }
      >
        {selectedAccount && (
          <Box>
            <GlassInfoCard severity="warning" sx={{ mb: 2 }}>
              Are you sure you want to close your account at <strong>{selectedAccount.bankName}</strong>?
              This action cannot be undone.
            </GlassInfoCard>

            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
              Account: {selectedAccount.accountNumber}
            </Typography>

            {selectedAccount.balances.some(b => b.balance !== 0) ? (
              <GlassInfoCard severity="error" sx={{ mt: 2 }}>
                You cannot close this account while it has a remaining balance. Please withdraw all funds first.
              </GlassInfoCard>
            ) : (
              <GlassTextField
                fullWidth
                label="Reason for closing (optional)"
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                margin="normal"
                multiline
                rows={3}
              />
            )}
          </Box>
        )}
      </GlassDialog>

      {/* Transfer Dialog */}
      <TransferDialog
        open={transferDialog}
        onClose={() => {
          setTransferDialog(false);
          setTransferSource(null);
        }}
        onTransfer={handleTransferSuccess}
        availableCurrencies={getAllCurrencies()}
        userDisplayName={sessionData?.selectedDisplayName}
        sessionData={sessionData}
        prefillFromType={transferSource ? "account" : null}
        prefillFromId={transferSource?.accountNumber || null}
        prefillFromName={transferSource ? `${transferSource.accountNumber} (${transferSource.accountHolder})` : null}
      />
      
      {/* Transaction Ledger Dialog */}
      <TransactionLedger
        open={transactionLedgerDialog}
        onClose={() => {
          setTransactionLedgerDialog(false);
          setLedgerAccount(null);
        }}
        entityType="account"
        entityId={ledgerAccount ? `${ledgerAccount.bankId}|${ledgerAccount.accountNumber}` : ''}
        entityName={ledgerAccount ? `${ledgerAccount.accountNumber} (${ledgerAccount.accountHolder})` : ''}
        sessionData={sessionData}
      />
    </Box>
  );
};

export default PlayerBankAccounts; 